import React, { useContext, useEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import {
  type RestMultiRowSurveyCard,
  type SurveyType,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import { type RestFortySixtyMiniModal3Modal } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';
import { FunnelButton } from '@bbkFunnelComponents/v2';
import Icon from '@bbkFunnelUtils/icon';
import { findSelectedReason } from '@bbkFunnelUtils/reason-other';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import LinkAction from '../buttons/link-action';
import DataReasons from './sections/data-reasons';
import ModalPerson from './sections/person';
import SimpleModalTitle from './sections/simple-title';

type Props = Pick<
  RestFortySixtyMiniModal3Modal['requires'],
  'info' | 'person' | 'forwards' | 'backwards'
> & {
  surveyType: SurveyType | '';
  questions: RestMultiRowSurveyCard['requires']['multi_row_survey'];
  response: Partial<RestMultiRowSurveyCard['provides']>;
  handleAction: Function;
};

const NoteModal3: React.FC<Props> = ({
  info,
  person,
  forwards,
  backwards,
  handleAction,
  surveyType,
  response,
  questions,
}) => {
  const theme = useTheme() as EmotionTheme;

  const refsPrefix = '123'; // FIXIT, should be real id of the card
  const refsContext = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsContext?.addRef(`${refsPrefix}`, titleRef), [titleRef]);

  const offerDetails = (
    <>
      {info.offer_details.map((i) => (
        <div className="d-flex align-items-center" key={i}>
          <div className="detailsIcon">
            <Icon name="check" />
          </div>
          <span>
            <CustomerHtml value={i} />
          </span>
        </div>
      ))}
    </>
  );

  const selectedReason = findSelectedReason(surveyType, response, questions);
  return (
    <div className="forty-sixty-mini-modal-3 forty-sixty-mini-modal">
      <div className="MMContainer">
        <div className="left">{person && <ModalPerson person={person} />}</div>
        <div className="right">
          <div className="wrap">
            <div className="reasons mb-3 mb-md-4 d-md-flex">
              <DataReasons
                reasons={info.account_data}
                reason={selectedReason}
              />
            </div>
            <span ref={titleRef}>
              <SimpleModalTitle>
                <CustomerHtml value={info.title} />
              </SimpleModalTitle>
            </span>
            <div className="note">
              <div className="details">
                <CustomerHtml value={info.details} />
              </div>
            </div>
            <div className="explanation mt-3 mb-3">{offerDetails}</div>
            {info.interest && (
              <div className="interest mb-3">{info.interest}</div>
            )}
            <div className="action-mm3">
              <FunnelButton
                specificStyles={{ fontWeight: 'bold', width: '100%' }}
                onClick={() => handleAction(backwards)}
              >
                <span style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                  {backwards.text}
                </span>
              </FunnelButton>
              {info.inline_disclaimer && (
                <span className="inline-disclaimer">
                  {info.inline_disclaimer}
                </span>
              )}
            </div>
            {info.disclaimer && (
              <div className="disclaimer">{info.disclaimer}</div>
            )}
            {info.offer_plea && (
              <div className="continue-action">
                <div style={{ paddingRight: '5px' }}>{info.offer_plea}</div>
                <LinkAction
                  style={{
                    color: theme.primaryColor,
                    fontWeight: 'bold',
                    display: 'inline-block',
                  }}
                  label={forwards.text}
                  onClick={() => handleAction(forwards, forwards.type)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteModal3;
